import { Intent as BlueprintIntent } from '@blueprintjs/core';
import { OverflowMenuHorizontal } from '@carbon/icons-react';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import {
  Body,
  Button,
  Loading,
  Menu,
  MenuItem,
  MenuItems,
  StaticAvatar,
  StaticBadge,
} from 'vet-bones/design-system/components';
import { alphaSort } from 'vet-bones/utils';

import { useAppDispatch } from 'src/ui/app/hooks';
import { DataTable, DataTableColumn } from 'src/ui/components/DataTable';
import { withConfirmationDs } from 'src/ui/components/withConfirmationDs';
import {
  ORGANIZATION_ENDPOINT_USER_TABLE_ADD,
  ORGANIZATION_ENDPOINT_USER_TABLE_COLUMN_ROLE,
  ORGANIZATION_ENDPOINT_USER_TABLE_CONFIRMATION_YES,
  ORGANIZATION_ENDPOINT_USER_TABLE_ROW_ACTIONS_MENU,
  ORGANIZATION_ENDPOINT_USER_TABLE_ROW_ACTIONS_MENU_ITEM,
  ORGANIZATION_ENDPOINT_USER_TABLE_ROW_AVATAR,
  ORGANIZATION_ENDPOINT_USER_TABLE_ROW_NAME,
  ORGANIZATION_ENDPOINT_USER_TABLE_ROW_ROLE,
  ORGANIZATION_ENDPOINT_USER_TABLE_SEARCH,
} from 'src/ui/constants/testIds';
import { getUserInfo } from 'src/ui/containers/endpoints/utils/getUserItem';
import { useInvalidateConnection } from 'src/ui/containers/endpoints/utils/invalidate';
import { handleMutationResult } from 'src/ui/containers/organization/hooks/helpers';
import { queueMessage } from 'src/ui/features/notifications';
import { DialogType, openedDialog } from 'src/ui/features/ui';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import {
  useListOrganizationConnectionAccessQuery,
  useListOrganizationConnectionsQuery,
  useRevokeMemberConnectionRolesMutation,
} from 'src/ui/graph/types';
import { useCurrentConnection } from 'src/ui/hooks/connection';
import { useOnError, useOnMutate } from 'src/ui/hooks/graph';
import * as copy from 'src/ui/templates/copy';

interface UserTableItem {
  id: string;
  initials: string;
  name?: string;
  email: string;
  roles: string[];
}

const LOCAL_COLUMNS: Array<DataTableColumn<UserTableItem>> = [
  {
    key: 'name',
    className: 'organization-table-row-user-name',
    header: copy.components.organization.userTable.columns.name,
    sortByAlpha: (item: UserTableItem) => item.name || item.email,
  },

  {
    key: 'action',
    className: 'data-table-row-actions',
    header: copy.components.organization.userTable.columns.action,
  },
];

const CLOUD_COLUMNS: Array<DataTableColumn<UserTableItem>> = [
  ...LOCAL_COLUMNS.slice(0, 1),
  {
    key: 'role',
    className: 'organization-table-row-user-role',
    header: copy.components.organization.userTable.columns.role,
    sortByAlpha: (item: UserTableItem) => item.roles.join(', '),
    testId: ORGANIZATION_ENDPOINT_USER_TABLE_COLUMN_ROLE,
  },
  ...LOCAL_COLUMNS.slice(1),
];

export const OrganizationEndpointUserTable: React.VFC = () => {
  const dispatch = useAppDispatch();
  const { org, id } = useParams<{ org: string; id: string }>();
  const onError = useOnError();
  const onMutate = useOnMutate();

  const { connection, isLoading: isConnectionLoading } = useCurrentConnection(
    id,
    org
  );
  const invalidateConnection = useInvalidateConnection(
    org,
    connection?.id,
    connection?.index
  );

  const {
    data: connectionAccessData,
    isLoading: isConnectionAccessLoading,
  } = useListOrganizationConnectionAccessQuery(
    graphQLClient,
    {
      id: connection?.id || '',
      org_domain: org,
    },
    { enabled: Boolean(connection?.id) }
  );
  const {
    data: connectionsData,
  } = useListOrganizationConnectionsQuery(graphQLClient, { org_domain: org });

  const {
    mutate: revokeMemberConnectionRoles,
  } = useRevokeMemberConnectionRolesMutation(graphQLClient, {
    onMutate,
    onError,
    onSuccess: (data, vars) => {
      invalidateConnection();

      const didSucceed = handleMutationResult(
        dispatch,
        data.revokeMemberConnectionRoles,
        copy.components.organization.userTable.notifications.removeUser.error,
        vars.input.emails,
        (res) => res.email
      );
      if (!didSucceed) {
        return;
      }

      dispatch(
        queueMessage({
          intent: BlueprintIntent.SUCCESS,
          message:
            copy.components.organization.userTable.notifications.removeUser
              .success,
        })
      );
    },
  });

  const isCloudOrFree = Boolean(
    connection?.isStardogCloud || connection?.isStardogFree
  );

  const userItems = React.useMemo(() => {
    const items: UserTableItem[] = [];
    connectionAccessData?.listOrganizationConnectionAccess?.forEach(
      (access) => {
        const userInfo = getUserInfo(access?.user);
        if (access && userInfo) {
          const roles = (
            access.stardog_roles || []
          ).filter((role): role is NonNullable<typeof role> => Boolean(role));
          items.push({ ...userInfo, roles });
        }
      }
    );
    items.sort(alphaSort((item) => item.name || item.email));

    return items;
  }, [connectionAccessData]);

  const defaultRoles = React.useMemo(() => {
    const defaultAccess = (
      connectionsData?.listDefaultOrganizationConnectionsAccess || []
    ).find((access) => access?.connection?.id === connection?.id);
    if (defaultAccess) {
      return (
        defaultAccess.stardog_roles?.filter((role): role is NonNullable<
          typeof role
        > => Boolean(role)) || []
      );
    }
    return null;
  }, [connection, connectionsData]);

  const searchFilter = (item: UserTableItem, query: string): boolean => {
    const trimmed = query.trim().toLowerCase();
    return (
      item.name?.toLowerCase().includes(trimmed) ||
      item.email.toLowerCase().includes(trimmed) ||
      item.roles.some((role) => role.toLowerCase().includes(trimmed))
    );
  };

  const handleAdd = () => {
    dispatch(openedDialog({ type: DialogType.SHARE_ORGANIZATION_ENDPOINT }));
  };

  const handleEdit = (user: UserTableItem) => {
    dispatch(
      openedDialog({
        type: DialogType.EDIT_SHARED_ORGANIZATION_ENDPOINT,
        data: { userId: user.id },
      })
    );
  };

  const handleRemove = (user: UserTableItem) => {
    withConfirmationDs({
      title:
        copy.components.organization.userTable.confirmations.removeUser.title,
      message: copy.components.organization.userTable.confirmations.removeUser.message(
        user.name || user.email
      ),
      yesPrompt:
        copy.components.organization.userTable.confirmations.removeUser.confirm,
      yesPromptTestId: ORGANIZATION_ENDPOINT_USER_TABLE_CONFIRMATION_YES,
      noPrompt:
        copy.components.organization.userTable.confirmations.removeUser.cancel,
      onConfirmationSuccess: () => {
        if (!connection?.id) {
          return;
        }

        revokeMemberConnectionRoles({
          input: {
            connectionId: connection.id,
            emails: [user.email],
            org_domain: org,
          },
        });
      },
    });
  };

  const renderRow = (user: UserTableItem, userIdx: number) => (
    <>
      <td className="organization-table-row-user-name">
        <div className="organization-table-row-user-name-cell">
          {user.initials ? (
            <StaticAvatar
              size="md"
              testId={ORGANIZATION_ENDPOINT_USER_TABLE_ROW_AVATAR(
                user.id,
                userIdx
              )}
              text={user.initials}
            />
          ) : null}
          <div className="organization-table-row-user-name-title">
            <Body
              ellipsize
              size="md"
              testId={ORGANIZATION_ENDPOINT_USER_TABLE_ROW_NAME(
                user.id,
                userIdx
              )}
            >
              {user.name || user.email}
            </Body>
            {user.name ? (
              <Body
                className="organization-table-row-user-name-subtitle"
                ellipsize
                size="sm"
              >
                {user.email}
              </Body>
            ) : null}
          </div>
        </div>
      </td>
      {isCloudOrFree ? (
        <td className="organization-table-row-user-roles">
          {user.roles.map((role, roleIdx) => (
            <StaticBadge
              key={role}
              size="md"
              testId={ORGANIZATION_ENDPOINT_USER_TABLE_ROW_ROLE(role, roleIdx)}
              type="default"
              variant="muted"
            >
              {role}
            </StaticBadge>
          ))}
        </td>
      ) : null}
      <td className="data-table-row-actions">
        {user.id ? (
          <Menu
            leftIcon={OverflowMenuHorizontal}
            menu={
              <MenuItems className="data-table-menu-items">
                {isCloudOrFree ? (
                  <MenuItem
                    leftIcon={null}
                    onClick={() => handleEdit(user)}
                    testId={ORGANIZATION_ENDPOINT_USER_TABLE_ROW_ACTIONS_MENU_ITEM(
                      'edit-roles'
                    )}
                    text={
                      copy.components.organization.userTable.actions.editRoles
                    }
                  />
                ) : null}
                <MenuItem
                  leftIcon={null}
                  onClick={() => handleRemove(user)}
                  testId={ORGANIZATION_ENDPOINT_USER_TABLE_ROW_ACTIONS_MENU_ITEM(
                    'remove-user'
                  )}
                  text={
                    copy.components.organization.userTable.actions.removeUser
                  }
                />
              </MenuItems>
            }
            size="sm"
            testId={ORGANIZATION_ENDPOINT_USER_TABLE_ROW_ACTIONS_MENU(
              user.id,
              userIdx
            )}
          />
        ) : null}
      </td>
    </>
  );

  if (isConnectionLoading || isConnectionAccessLoading) {
    return (
      <div className="organization-dashboard-loading">
        <Loading size="lg" />
      </div>
    );
  }

  return (
    <DataTable
      className="organization-table"
      columns={isCloudOrFree ? CLOUD_COLUMNS : LOCAL_COLUMNS}
      data={userItems}
      getRowKey={(user) => user.id}
      preRow={
        defaultRoles
          ? renderRow(
              {
                id: '',
                initials: '',
                email: 'All Members',
                roles: defaultRoles,
              },
              -1
            )
          : undefined
      }
      renderRow={renderRow}
      searchActions={
        <Button
          onClick={handleAdd}
          size="md"
          testId={ORGANIZATION_ENDPOINT_USER_TABLE_ADD}
          variant="tertiary-accent"
        >
          {copy.components.organization.userTable.add}
        </Button>
      }
      searchFilter={searchFilter}
      searchInputTestId={ORGANIZATION_ENDPOINT_USER_TABLE_SEARCH}
      searchPlaceholder={copy.components.organization.userTable.search}
      title={copy.components.organization.userTable.title(userItems.length)}
    />
  );
};
